import type { IServerSideSelectionState, SelectionEventSourceType } from "ag-grid-enterprise";
import type { SelectionChangedEvent } from "~/components/grid/AgGrid";

type ServerSideSelectionStateUnion = SelectionChangedEvent<Record<string, unknown>>["serverSideState"];

// setServerSideSelectionState emits "api"; its callers write their own mirrors.
const AUTHORITATIVE_SELECTION_SOURCES: ReadonlySet<SelectionEventSourceType> = new Set([
	"apiSelectAll",
	"apiSelectAllCurrentPage",
	"apiSelectAllFiltered",
	"checkboxSelected",
	"keyboardSelectAll",
	"rowClicked",
	"spaceKey",
	"uiSelectAll",
	"uiSelectAllCurrentPage",
	"uiSelectAllFiltered"
]);

const isAuthoritativeSelectionSource = (source: SelectionEventSourceType): boolean =>
	AUTHORITATIVE_SELECTION_SOURCES.has(source);

// AG Grid replays a selection persisted in initialState under this source, before any row block has loaded.
const isRestoredSelectionSource = (source: SelectionEventSourceType): boolean => source === "gridInitializing";

const isFlatSelectionState = (state: ServerSideSelectionStateUnion): state is IServerSideSelectionState =>
	state != null && typeof (state as IServerSideSelectionState).selectAll === "boolean";

// AG Grid reports select-all-filtered without loading the matching rows, so their ids are unknowable here.
const isSelectAllActive = (state: ServerSideSelectionStateUnion): boolean =>
	isFlatSelectionState(state) && state.selectAll;

// Under selectAll the toggled ids are the exclusions, so they never name the selection.
const readToggledNodeIds = (state: ServerSideSelectionStateUnion): string[] =>
	isFlatSelectionState(state) && !state.selectAll ? state.toggledNodes : [];

// AG Grid resolves a selected id to row data only once its block has loaded, so a state can outrun the rows.
const hasUnresolvedToggledNodes = (state: ServerSideSelectionStateUnion, resolvedRowCount: number): boolean =>
	readToggledNodeIds(state).length > resolvedRowCount;

const isSelectionStateEmpty = (state: ServerSideSelectionStateUnion): boolean => {
	if (state == null) return true;
	if (!isFlatSelectionState(state)) return false;
	return !state.selectAll && state.toggledNodes.length === 0;
};

const computeSelectedCount = (
	state: IServerSideSelectionState | null,
	displayedRowCount: number,
	fallbackCount: number
): number => {
	if (state?.selectAll) return Math.max(0, displayedRowCount - state.toggledNodes.length);
	if (state) return state.toggledNodes.length;
	return fallbackCount;
};

const applyToggledOff = <TRow>(rows: TRow[], toggledNodeIds: string[], getRowId: (row: TRow) => string): TRow[] => {
	if (toggledNodeIds.length === 0) return rows;
	const toggledOff = new Set(toggledNodeIds);
	return rows.filter((row) => !toggledOff.has(getRowId(row)));
};

const retainSelectedRowData = <TRow>(
	retained: Map<string, TRow>,
	liveSelectedRows: TRow[],
	state: IServerSideSelectionState | null,
	getRowId: (row: TRow) => string
): void => {
	if (!state || state.selectAll) {
		retained.clear();
		return;
	}
	for (const row of liveSelectedRows) {
		retained.set(getRowId(row), row);
	}
	const selectedIds = new Set(state.toggledNodes);
	for (const id of retained.keys()) {
		if (!selectedIds.has(id)) retained.delete(id);
	}
};

const resolveRetainedSelectedRows = <TRow>(
	retained: ReadonlyMap<string, TRow>,
	liveSelectedRows: TRow[],
	state: IServerSideSelectionState | null,
	getRowId: (row: TRow) => string
): TRow[] => {
	if (!state || state.selectAll) return liveSelectedRows;
	const liveRowsById = new Map(liveSelectedRows.map((row) => [getRowId(row), row]));
	return state.toggledNodes
		.map((id) => liveRowsById.get(id) ?? retained.get(id))
		.filter((row): row is TRow => row !== undefined);
};

const ssrmSelection = {
	applyToggledOff,
	computeSelectedCount,
	hasUnresolvedToggledNodes,
	isAuthoritativeSelectionSource,
	isFlatSelectionState,
	isRestoredSelectionSource,
	isSelectAllActive,
	isSelectionStateEmpty,
	readToggledNodeIds,
	resolveRetainedSelectedRows,
	retainSelectedRowData
};

export default ssrmSelection;
